import PropTypes from 'prop-types';
import { Grid, TextField, InputAdornment, MenuItem } from '@mui/material';
import { SearchOutlined } from '@ant-design/icons';

/**
 * Reusable filter bar component for tables
 *
 * @example
 * <TableFilters
 *   filters={[
 *     {
 *       type: 'search',
 *       value: search,
 *       onChange: (value) => setSearch(value),
 *       placeholder: 'Search users...'
 *     },
 *     {
 *       type: 'select',
 *       label: 'Status',
 *       value: statusFilter,
 *       onChange: (value) => setStatusFilter(value),
 *       options: [
 *         { value: 'all', label: 'All Statuses' },
 *         { value: 'active', label: 'Active' },
 *         { value: 'inactive', label: 'Inactive' }
 *       ]
 *     }
 *   ]}
 * />
 */
const TableFilters = ({ filters = [], spacing = 2 }) => {
  const renderFilter = (filter, index) => {
    const gridSize = filter.gridSize || { xs: 12, sm: 6, md: filter.type === 'search' ? 6 : 3 };

    switch (filter.type) {
      case 'search':
        return (
          <Grid key={index} size={gridSize}>
            <TextField
              fullWidth
              placeholder={filter.placeholder || 'Search...'}
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
              onKeyPress={filter.onKeyPress}
              disabled={filter.disabled}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined />
                    </InputAdornment>
                  )
                }
              }}
            />
          </Grid>
        );

      case 'select':
        return (
          <Grid key={index} size={gridSize}>
            <TextField
              fullWidth
              select
              label={filter.label}
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
              disabled={filter.disabled}
            >
              {filter.options?.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        );

      case 'date':
        return (
          <Grid key={index} size={gridSize}>
            <TextField
              fullWidth
              type="date"
              label={filter.label}
              value={filter.value || ''}
              onChange={(e) => filter.onChange(e.target.value)}
              disabled={filter.disabled}
              slotProps={{
                inputLabel: { shrink: true }
              }}
            />
          </Grid>
        );

      case 'custom':
        return (
          <Grid key={index} size={gridSize}>
            {filter.render()}
          </Grid>
        );

      default:
        return null;
    }
  };

  if (filters.length === 0) {
    return null;
  }

  return (
    <Grid container spacing={spacing} sx={{ mb: 3 }}>
      {filters.map((filter, index) => renderFilter(filter, index))}
    </Grid>
  );
};

TableFilters.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.oneOf(['search', 'select', 'date', 'custom']).isRequired,
      value: PropTypes.any,
      onChange: PropTypes.func,
      label: PropTypes.string,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired
        })
      ),
      disabled: PropTypes.bool,
      onKeyPress: PropTypes.func,
      gridSize: PropTypes.object,
      render: PropTypes.func
    })
  ),
  spacing: PropTypes.number
};

export default TableFilters;
